<template>
  <b-container v-if="order">
    <b-row v-if="order" class="base-form mb-3">
      <b-col sm="12" md="6" lg="3">
        <b-form-group label="Status">
          {{ order.status | statusFormat }}
        </b-form-group>
        <b-form-group v-if="order.facility" label="Hub">
          {{ order.facility.name }}
        </b-form-group>
        <b-form-group v-if="order.network" label="Network">
          {{ order.network.name }}
        </b-form-group>
        <b-form-group label="Fulfillment Method">
          {{ order.fulfillment_type | fulfillmentTypeFormat | capitalize }}
        </b-form-group>
      </b-col>
      <b-col sm="12" md="6" lg="3">
        <b-form-group label="Customer">
          <!-- <b-link :to="`/customers/${order.user_id}`"> -->
          {{ order.user.first_name }} {{ order.user.last_name }}
          <!-- </b-link> -->
        </b-form-group>
        <b-form-group label="Email">
          {{ order.user.email }}
        </b-form-group>
        <b-form-group label="Phone">
          {{ order.user.phone | phone }}
        </b-form-group>
        <b-form-group label="Utensil Quantity">
          {{ order.utensil_quantity }}
        </b-form-group>
      </b-col>
      <b-col sm="12" md="6" lg="3">
        <b-form-group label="Order Placed">
          {{ order.placed | formatDateTime }}
        </b-form-group>
        <b-form-group v-if="order.facility" label="Kitchen Ready Time">
          {{ order.kitchenReadyTime | formatDateTime }}
        </b-form-group>
        <b-form-group v-if="order.facility" label="Facility Ready Time">
          {{ order.facilityReadyTime | formatDateTime }}
        </b-form-group>
        <b-form-group label="Fulfillment Time">
          {{ order.fulfillment_time | formatDateTime }}
        </b-form-group>
      </b-col>
      <b-col v-if="order.address" sm="12" md="6" lg="3">
        <b-form-group label="Delivery">
          <b-link :to="`/deliveries/${order.delivery_id}`">
            #{{ order.delivery_id }}
          </b-link>
        </b-form-group>
        <b-form-group label="Address">
          {{ order.address | formatAddressStreet }}
        </b-form-group>
        <b-form-group label="Building">
          {{ order.address.street }}
        </b-form-group>
        <b-form-group label="Delivery Notes">
          {{ order.notes ? order.notes : 'None' }}
        </b-form-group>
      </b-col>
    </b-row>
    <b-row
      v-if="showStatusControls"
      class="base-form mb-3 pb-3 px-3"
      no-gutters
    >
      <b-col cols="12" class="mb-2 pb-0">
        <span class="col-form-label">Order Status</span>
      </b-col>
      <b-col cols="12" sm="3">
        <div class="d-flex">
          <b-select
            v-model="orderStatus"
            class="mr-2"
            :options="orderStatusOptions"
          />
          <checkbox-button
            v-if="orderStatus !== order.status"
            :value="true"
            class="mr-2"
            @click.native="setOrderStatus"
          />
        </div>
      </b-col>
      <b-col cols="12" sm="9" class="pl-0 pl-sm-3">
        <p class="helper-text mt-2">
          By updating the entire order status, you will update all of the items
          to the same status.
        </p>
      </b-col>
    </b-row>
    <b-container class="base-order-form px-3 mb-3">
      <b-row
        v-for="centerOrder in order.fulfillment_center_orders"
        :key="centerOrder.id"
        class="fulfillment-center no-gutters overflow-auto"
      >
        <b-col cols="12" class="mb-2">
          <span class="col-form-label">
            {{ centerOrder.fulfillment_center.name }}
          </span>
        </b-col>
        <b-col
          v-for="orderItem in centerOrder.order_items"
          :key="orderItem.id"
          class="mb-2"
          cols="12"
        >
          <b-row no-gutters>
            <b-col
              v-if="showStatusControls"
              cols="12"
              md="3"
              class="mb-2 mb-sm-0"
            >
              <div class="d-flex">
                <b-select
                  v-model="orderItemStatuses[orderItem.id]"
                  class="mr-2"
                  :options="orderStatusOptions"
                  @change="onOrderItemStatusChange(orderItem.id)"
                />
                <checkbox-button
                  v-if="orderItem.status !== orderItemStatuses[orderItem.id]"
                  :value="true"
                  class="mr-2"
                  @click.native="setOrderItemStatus(orderItem)"
                />
              </div>
            </b-col>
            <b-col
              :md="order.status === 'refunded' ? 10 : 7"
              cols="9"
              class="pl-0 pl-sm-3"
            >
              <b-row class="mt-0 mt-sm-1">
                <b-col cols="1">{{ orderItem.quantity }}x</b-col>
                <b-col cols="11">
                  {{ orderItem.name }}
                  <ul
                    v-if="orderItem.order_item_attributes || orderItem.notes"
                    class="mb-0"
                  >
                    <li
                      v-for="orderItemAttribute in orderItem.order_item_attributes"
                      :key="orderItemAttribute.id"
                    >
                      {{ orderItemAttribute.name }} -
                      {{ orderItemAttribute.value }} +
                      {{ (orderItemAttribute.price / 100) | currency }}
                    </li>
                    <li v-if="orderItem.notes">
                      <strong>Special Instructions:</strong>
                      {{ orderItem.notes }}
                    </li>
                  </ul>
                </b-col>
              </b-row>
            </b-col>
            <b-col cols="3" md="2" class="text-right">
              {{ (orderItem.price / 100) | currency }}
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="base-form pb-3">
        <b-col cols="6" class="mb-2"> Subtotal </b-col>
        <b-col cols="6" class="text-right">
          {{ (order.subtotal / 100) | currency }}
        </b-col>
        <b-col cols="6" class="h7"> Customer Total </b-col>
        <b-col cols="6" class="text-right">
          {{ (order.order_total / 100) | currency }}
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import ResourceAPI from '../../services/ResourceAPI';

export default {
  props: {
    order: {
      type: Object,
      default() {
        return { id: null };
      },
    },
  },
  data() {
    return {
      orderStatus: null,
      orderItemStatuses: {},
      orderStatusOptions: [
        {
          value: null,
          text: 'Choose status',
        },
        {
          value: 'placed',
          text: 'Placed',
        },
        {
          value: 'confirmed',
          text: 'Confirmed',
        },
        {
          value: 'preparing',
          text: 'Preparing',
        },
        {
          value: 'prepared',
          text: 'Prepared',
        },
        {
          value: 'enroute_expo',
          text: 'En Route Expo',
        },
        {
          value: 'compiling',
          text: 'Compiling',
        },
        {
          value: 'enroute_delivery_expo',
          text: 'En Route Delivery Expo',
        },
        {
          value: 'delivery_compiling',
          text: 'Delivery Compiling',
        },
        {
          value: 'enroute_dispatch',
          text: 'Enroute Dispatch',
        },
        {
          value: 'ready',
          text: 'Ready',
        },
        {
          value: 'enroute_customer',
          text: 'Enroute',
        },
        {
          value: 'completed',
          text: 'Completed',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['isAdmin']),
    showStatusControls() {
      return this.isAdmin && this.order.status !== 'refunded';
    },
  },
  watch: {
    order(val) {
      if (val) {
        this.updateLocalStatuses();
      }
    },
  },
  created() {
    this.updateLocalStatuses();
  },
  methods: {
    setOrderStatus() {
      ResourceAPI.makePatchRequest(`kms/orders/${this.order.id}/status`, {
        status: this.orderStatus,
      }).then(() => {
        this.$emit('saved');
      });
    },
    setOrderItemStatus(orderItem) {
      ResourceAPI.makePatchRequest(
        `kms/orders/${this.order.id}/order-item-status`,
        {
          orderItems: [
            {
              id: orderItem.id,
              status: this.orderItemStatuses[orderItem.id],
            },
          ],
        }
      ).then(() => {
        this.$emit('saved');
      });
    },
    updateLocalStatuses() {
      this.orderStatus = this.order.status;
      this.order.fulfillment_center_orders.forEach((centerOrder) => {
        centerOrder.order_items.forEach((orderItem) => {
          this.orderItemStatuses[orderItem.id] = orderItem.status;
        });
      });
    },
    onOrderItemStatusChange(orderItemId) {
      this.orderItemStatuses = {
        ...this.orderItemStatuses,
        orderItemId: this.orderItemStatuses[orderItemId],
      };
    },
  },
};
</script>
